import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ErpDataGrid.module.css';

const TOP_N = 10;

function rankOptions(options, query) {
  const q = query.trim().toLowerCase();
  const list = q
    ? options.filter((opt) => opt.label.toLowerCase().includes(q))
    : options;
  const ranked = [...list].sort((a, b) => {
    if (q) {
      const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
    }
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
  return ranked.slice(0, TOP_N);
}

function SearchFilterDropdown({ fieldName, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((opt) => String(opt.id) === String(value));
  const suggestions = useMemo(() => rankOptions(options, query), [options, query]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  const choose = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className={styles.filterDropdown} ref={wrapRef}>
      <button
        type="button"
        className={`${styles.filterTrigger} ${value ? styles.filterTriggerActive : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${fieldName} filter`}
        onClick={() => {
          setOpen((prev) => !prev);
          setQuery('');
        }}
      >
        <span className={styles.filterTriggerLabel}>{selected?.label || 'Any'}</span>
        <span className={styles.filterCaret} aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div className={styles.filterMenu} role="listbox">
          <input
            ref={inputRef}
            type="search"
            className={styles.filterSearch}
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={`Search ${fieldName}`}
          />
          <button
            type="button"
            className={`${styles.filterOption} ${!value ? styles.filterOptionActive : ''}`}
            onClick={() => choose('')}
          >
            Any
          </button>
          {suggestions.length === 0 ? (
            <div className={styles.filterEmpty}>No matches</div>
          ) : (
            suggestions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`${styles.filterOption} ${
                  String(value) === String(opt.id) ? styles.filterOptionActive : ''
                }`}
                onClick={() => choose(opt.id)}
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const SearchFilterBar = ({ fields, optionsByField, values, onChange, onClear }) => {
  const hasActive = fields.some((field) => Boolean(values[field.name]));
  if (!fields.length) return null;

  return (
    <div className={styles.filterBar}>
      {fields.map((field) => (
        <div key={field.name} className={styles.filterItem}>
          <span className={styles.filterLabel}>{field.name}</span>
          <SearchFilterDropdown
            fieldName={field.name}
            options={optionsByField[field.name] || []}
            value={values[field.name] || ''}
            onChange={(next) => onChange(field.name, next)}
          />
        </div>
      ))}
      <button
        type="button"
        className={styles.clearFiltersBtn}
        onClick={onClear}
        disabled={!hasActive}
      >
        Clear filters
      </button>
    </div>
  );
};

export default SearchFilterBar;
